/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import {
  cleanupFailedAssetUpload,
  insertLocalAsset,
  LocalAsset,
} from "actions/AssetActions";
import { saveProject } from "actions/ProjectActions";
import { addToast } from "actions/ToastMessageActions";
import getAudioPlayerInstance from "AudioPlayerInstance";
import * as ActionTypes from "constants/ActionTypes";
import { NewRelicEvent } from "constants/Analytics";
import { BlockTypes } from "constants/BlockTypes";
import { ClipUploadStates } from "constants/ClipUploadStates";
import isEqual from "lodash.isequal";
import { InsertDirection } from "reducers/DragAndDropReducer";
import {
  ClipUploadedAction,
  ReplaceClipsAction,
  SetClipIsBackgroundAction,
} from "types/Actions";
import { AppAction } from "types/AppAction";
import { Block, blockTokens, TranscriptionBlock } from "types/Block";
import {
  Clip,
  ClipId,
  ClipWithPlaylistTimes,
  isClipTypeWithoutAssetSupport,
  isClipUploading,
  isClipWithAsset,
  WaveformClip,
} from "types/Clip";
import {
  clipsFromBlock,
  clipWithoutTimes,
  findClipsInRange,
  findClipsInSelection,
  mapClipsToClipsWithTimes,
  Range,
  rangesIntersect,
  Selection,
} from "utilities/ClipUtilities";
import * as ProjectUtilities from "utilities/ProjectUtilities";
import {
  trimBeginningIfSplittable,
  trimEndIfSplittable,
} from "utilities/TrimUtilities";
import { v4 as uuidv4 } from "uuid";
import { Asset, AssetSet } from "types/Asset";
import { MediaType } from "@phonos/types";
import { SelectionState } from "reducers/SelectionReducer";
import { generateClipTranscript } from "utilities/TranscriptUtilities";
import { BlockIdStabilizer } from "utilities/BlockIDs";
import {
  getMediaInfo,
  getMessageFromMediaDecodeError,
  MediaDecodeDimensionsError,
  MediaDecodeDurationError,
  MediaDecodeError,
  MediaDecodeFormatError,
  validateMediaContentType,
} from "utilities/MediaUtilities";

export function setClipIsBackground(
  clipId: ClipId,
  isBackground: boolean
): AppAction<Promise<SetClipIsBackgroundAction>> {
  return async (dispatch): Promise<SetClipIsBackgroundAction> => {
    const actionResult = dispatch({
      type: ActionTypes.SET_CLIP_IS_BACKGROUND,
      clipId,
      isBackground,
    });
    dispatch(saveProject());
    return actionResult;
  };
}

export function insertFileAdjacentToRange({
  file,
  range,
  insertDirection,
}: {
  file: File;
  range: Range;
  insertDirection: InsertDirection;
}): AppAction<Promise<ReplaceClipsAction | void>> {
  return async (dispatch): Promise<ReplaceClipsAction | void> => {
    let insertAtTime;
    switch (insertDirection) {
      case InsertDirection.BEFORE:
        insertAtTime = range.startTime;
        break;
      case InsertDirection.AFTER:
        insertAtTime = range.endTime;
        break;
      default:
        console.assert(
          `Cannot insert file adjacent to block or token for insert direction ${insertDirection}.`
        );
        // TODO: Add toast here for user? Send error to New Relic?
        return;
    }

    // TODO: Should this be wrapped in recordHistory()???
    return dispatch(insertFileAtTime({ file, playlistOffset: insertAtTime }));
  };
}

export function insertFileAtTime({
  file,
  clipId,
  playlistOffset,
}: {
  file: File;
  clipId?: ClipId;
  playlistOffset: number;
}): AppAction<Promise<ReplaceClipsAction | void>> {
  return async (dispatch): Promise<ReplaceClipsAction | void> => {
    const clipToInsert: WaveformClip = {
      id: clipId || uuidv4(),
      type: BlockTypes.WAVEFORM,
      offset: 0,
      duration: 1,
      name: file.name,
      uploadState: ClipUploadStates.STARTED,
      assetId: null,
      fadeIn: false,
      fadeOut: false,
      isBackground: false,
    };

    const actionResult = await dispatch(
      insertClipAtTime(clipToInsert, playlistOffset)
    );

    await dispatch(uploadFileForClip(file, clipToInsert));

    dispatch(saveProject());

    return actionResult;
  };
}

export function insertFileAtPlayhead({
  file,
  clipId,
}: {
  file: File;
  clipId?: ClipId;
}): AppAction<Promise<ReplaceClipsAction | void>> {
  return async (dispatch, getState): Promise<ReplaceClipsAction | void> => {
    const {
      player: { currentTime },
      clips,
    } = getState();

    const insertTime = preventSplittingWaveformClipsAtPlayhead(
      currentTime,
      clips
    );

    return dispatch(
      insertFileAtTime({ file, clipId, playlistOffset: insertTime })
    );
  };
}

function preventSplittingWaveformClipsAtPlayhead(
  currentTime: number,
  clips: Clip[]
): number {
  const clipsWithTimes = mapClipsToClipsWithTimes(clips);
  // Background clips could cause this to return multiple clips, so grab the last one.
  const clipAtPlayhead = clipsWithTimes
    .filter((clip) =>
      rangesIntersect(clip, { startTime: currentTime, endTime: currentTime })
    )
    .pop();

  if (clipAtPlayhead && clipAtPlayhead.type === BlockTypes.WAVEFORM) {
    if (
      currentTime - clipAtPlayhead.startTime <
      clipAtPlayhead.endTime - currentTime
    ) {
      return clipAtPlayhead.startTime;
    } else {
      return clipAtPlayhead.endTime;
    }
  } else {
    return currentTime;
  }
}

interface GetLocalAssetForFileParams {
  file: File;
  hasStudioVideoUploadEnabled: boolean;
}

export class MediaDecodeUnknownDurationError extends MediaDecodeError {
  constructor(fileType: string, mediaType: MediaType) {
    super(fileType, mediaType);
    this.name = "MediaDecodeUnknownDurationError";
  }
}

export async function getLocalAssetForFile({
  file,
  hasStudioVideoUploadEnabled,
}: GetLocalAssetForFileParams): Promise<LocalAsset> {
  const assetId = uuidv4();

  const mediaInfo = await getMediaInfo(file);
  let mediaType = mediaInfo.mediaType;

  // If the duration is not known (e.g. fragmented MP4), throw an error.
  // ESQA can tolerate 'unknown duration' files, but we need to throw an error here.
  // Unknown duration files currently crash the studio!
  // TODO: Server side duration calculation can be added to handle this case
  if (typeof mediaInfo.durationMs !== "number") {
    throw new MediaDecodeUnknownDurationError(file.type, mediaInfo.mediaType);
  }

  // Always force audio mediaType when video upload is disabled
  if (!hasStudioVideoUploadEnabled) {
    mediaType = MediaType.AUDIO;
  }

  // let the slow audio decode step happen in parallel
  getAudioPlayerInstance().loadLocalAsset(assetId, file);
  return {
    file: file,
    assetId: assetId,
    duration: mediaInfo.durationMs,
    videoDimensions: mediaInfo.dimensions,
    videoCodec: mediaInfo.videoCodec,
    mediaType,
    participants: [
      {
        id: assetId,
        unidentifiedSpeakerNumber: 0,
        media: {
          mediaType,
          id: assetId,
        },
      },
    ],
  };
}

const MAX_DURATION_IN_MILLISECONDS = 2 * 60 * 60 * 1000; // 2 hours
// Check that neither dimension of the video isbigger than "DCI 4K" = 4096px
const MAX_VIDEO_DIMENSIONS = 4096;
export function uploadFileForClip(
  file: File,
  clip: Clip
): AppAction<Promise<ClipUploadedAction | void>> {
  return async (dispatch, getState): Promise<ClipUploadedAction | void> => {
    let localAsset: LocalAsset | undefined;
    let user = getState().currentUser;
    let hasStudioVideoUploadEnabled = !!user?.studioVideoUploadEnabled;

    try {
      newrelic.addPageAction(NewRelicEvent.UPLOAD_FILE_FOR_CLIP_STARTED, {
        fileType: file.type,
        fileSize: file.size,
      });
      // Fast-reject for invalid content types
      validateMediaContentType(file);

      localAsset = await getLocalAssetForFile({
        file,
        hasStudioVideoUploadEnabled,
      });

      // A duration of 0 means there are no audio or video tracks (e.g. An m4a with no tracks in it)
      // Reject this early as it will fail when processing.
      if (localAsset.duration <= 0) {
        throw new MediaDecodeError(file.type, localAsset.mediaType);
      }

      // If a file is longer than 2 hours, reject it. It will break when someone tries to transcribe it.
      if (localAsset.duration > MAX_DURATION_IN_MILLISECONDS) {
        throw new MediaDecodeDurationError(file.type, localAsset.mediaType);
      }

      // Check the video dimensions to make sure they're not too large.
      // The reason for this limit is that videos with very large pixel dimensions (e.g. 16K)
      // are very slow to process in ffmpeg, would cause other jobs to get queued up on the video workers.
      // ESQA does not need this limit because it does not transcode videos.
      if (
        localAsset.mediaType === MediaType.VIDEO &&
        localAsset.videoDimensions
      ) {
        const { width, height } = localAsset.videoDimensions;
        if (width && height) {
          const largestDimension = Math.max(width, height);

          // Check that the video isn't bigger than "DCI 4K"
          if (largestDimension > MAX_VIDEO_DIMENSIONS) {
            throw new MediaDecodeDimensionsError(
              file.type,
              localAsset.mediaType
            );
          }
        }
      }

      // Specific check for Quicktime MOV files that we can't parse the codec for.
      // i.e. ProRes, which we cannot support in the studio yet.
      // See https://jira.corp.adobe.com/browse/PHONOS-3377
      if (file.type === "video/quicktime" && !localAsset.videoCodec) {
        throw new MediaDecodeFormatError(file.type, localAsset.mediaType);
      }
    } catch (e) {
      newrelic.noticeError(e);
      const message = getMessageFromMediaDecodeError(e);
      newrelic.addPageAction(
        NewRelicEvent.UPLOAD_FILE_FOR_CLIP_REJECTED_CLIENT_SIDE,
        {
          fileType: file.type,
          fileSize: file.size,
          mediaType: localAsset?.mediaType ?? "",
          duration: localAsset?.duration ?? "",
          videoWidthInPixels: localAsset?.videoDimensions?.width ?? "",
          videoHeightInPixels: localAsset?.videoDimensions?.height ?? "",
          videoCodec: localAsset?.videoCodec ?? "",
          errorType: e.name,
          errorMessage: message,
        }
      );
      dispatch(addToast({ message }));
      dispatch({
        type: ActionTypes.REPLACE_CLIPS,
        clipsToReplace: [clip],
        clipsToInsert: [],
      });
      return;
    }

    await dispatch({
      type: ActionTypes.SET_CLIP_ESTIMATED_DURATION,
      clipId: clip.id,
      duration: localAsset.duration,
    });

    await dispatch(uploadAssetForClip(localAsset, clip));
  };
}

function uploadAssetForClip(
  localAsset: LocalAsset,
  clip: Clip
): AppAction<Promise<ClipUploadedAction | void>> {
  return async (dispatch, getState): Promise<ClipUploadedAction | void> => {
    const { project } = getState();

    try {
      dispatch(
        insertLocalAsset({
          localAsset,
          clipId: clip.id,
        })
      );

      dispatch({
        type: ActionTypes.ASSET_UPLOAD_STARTED,
        assetId: localAsset.assetId,
      });

      const _ = await ProjectUtilities.uploadAsset({
        project: project,
        file: localAsset.file,
        assetId: localAsset.assetId,
        duration: localAsset.duration,
        mediaType: localAsset.mediaType,
        onProgress: (progress: number) => {
          dispatch({
            type: ActionTypes.ASSET_UPLOAD_PROGRESS,
            assetId: localAsset.assetId,
            progress: progress,
          });
        },
      });

      newrelic.addPageAction(NewRelicEvent.UPLOAD_FILE_FOR_CLIP_COMPLETED, {
        fileType: localAsset.file.type,
        fileSize: localAsset.file.size,
        mediaType: localAsset.mediaType,
        duration: localAsset.duration,
        videoWidthInPixels: localAsset.videoDimensions?.width ?? "",
        videoHeightInPixels: localAsset?.videoDimensions?.height ?? "",
        videoCodec: localAsset?.videoCodec ?? "",
      });

      return dispatch({
        type: ActionTypes.CLIP_UPLOADED,
        clipId: clip.id,
      });
    } catch (e) {
      console.error(e);
      newrelic.noticeError(e, {
        projectId: project.id,
        ownerId: project.owner_id,
        fileType: localAsset.file.type,
        fileSize: localAsset.file.size,
        mediaType: localAsset.mediaType,
        duration: localAsset.duration,
        videoWidthInPixels: localAsset?.videoDimensions?.width ?? "",
        videoHeightInPixels: localAsset?.videoDimensions?.height ?? "",
        videoCodec: localAsset?.videoCodec ?? "",
      });
      dispatch(addToast({ message: I18n.t("upload.error") }));
      dispatch(cleanupFailedAssetUpload(localAsset.assetId));
    }
  };
}

export function deleteClip({
  clipId,
}: {
  clipId: ClipId;
}): AppAction<Promise<ReplaceClipsAction | void>> {
  return async (dispatch, getState): Promise<ReplaceClipsAction | void> => {
    const { clips } = getState();
    const clip = clips.find((clip) => clip.id === clipId);
    if (!clip) {
      return;
    }

    const actionResult = await dispatch({
      type: ActionTypes.REPLACE_CLIPS,
      clipsToReplace: [clip],
      clipsToInsert: [],
    });

    dispatch(saveProject());

    return actionResult;
  };
}

export function splitClipAtPlayhead(): AppAction<
  Promise<ReplaceClipsAction | void>
> {
  return async (dispatch, getState): Promise<ReplaceClipsAction | void> => {
    const {
      clips,
      player: { currentTime },
    } = getState();
    const clipsWithTimes = mapClipsToClipsWithTimes(clips);
    const clipWithTime = clipsWithTimes.find((clip) =>
      rangesIntersect(clip, { startTime: currentTime, endTime: currentTime })
    );
    if (!clipWithTime || clipWithTime.type !== BlockTypes.WAVEFORM) {
      return;
    }

    if (
      currentTime < clipWithTime.startTime ||
      currentTime > clipWithTime.endTime
    ) {
      return;
    }

    const endClipDuration = clipWithTime.endTime - currentTime;

    const beginningClip: Clip = {
      ...clipWithTime,
      id: uuidv4(),
      duration: clipWithTime.duration - endClipDuration,
    };

    const endClip: Clip = {
      ...clipWithTime,
      id: uuidv4(),
      offset: clipWithTime.offset + beginningClip.duration,
      duration: endClipDuration,
    };

    const actionResult = await dispatch({
      type: ActionTypes.REPLACE_CLIPS,
      clipsToReplace: [clipWithTime],
      clipsToInsert: [beginningClip, endClip],
    });

    dispatch(saveProject());

    return actionResult;
  };
}

export function insertClipAtTime(
  clip: Clip,
  playlistOffsetMs: number
): AppAction<ReplaceClipsAction | void> {
  return insertClipsAtTime([clip], playlistOffsetMs);
}

export function insertClipAtEnd(
  clip: Clip
): AppAction<ReplaceClipsAction | void> {
  return (dispatch, getState): ReplaceClipsAction | void => {
    const { clips } = getState();
    const clipsWithTimes = mapClipsToClipsWithTimes(clips);

    // Find the end time of the last clip
    const lastClip = clipsWithTimes[clipsWithTimes.length - 1];
    const playlistOffset = lastClip ? lastClip.endTime : 0;

    return dispatch(insertClipAtTime(clip, playlistOffset));
  };
}

export function buildSelectionAtTime(
  playlistOffsetMs: number,
  selection: SelectionState,
  clips: Clip[]
): Selection {
  let clipIdToSplit = selection?.clipIds?.[0]; // TODO: SELECTION SHOULDN'T BE USED FOR INSERTING AT TIME; VERY CONFUSING!
  if (!clipIdToSplit) {
    const clipsAtPlaylistOffset = findClipsInRange(clips, {
      startTime: playlistOffsetMs,
      endTime: playlistOffsetMs,
    });
    const lastClipAtPlayhead =
      clipsAtPlaylistOffset[clipsAtPlaylistOffset.length - 1];
    clipIdToSplit = lastClipAtPlayhead?.id;
  }
  return {
    startTime: playlistOffsetMs,
    endTime: playlistOffsetMs,
    clipIds: [clipIdToSplit],
  };
}

export function insertClipsAtTime(
  newClips: Clip[],
  playlistOffsetMs: number
): AppAction<ReplaceClipsAction | void> {
  return (dispatch, getState): ReplaceClipsAction | void => {
    const { clips, selection } = getState();

    const selectionAtTime = buildSelectionAtTime(
      playlistOffsetMs,
      selection,
      clips
    );
    return dispatch(
      replaceSelectionWithClips(clips, selectionAtTime, newClips)
    );
  };
}

function buildNewClipsToReplaceSelection(
  selection: Selection,
  clipsInSelection: ClipWithPlaylistTimes[],
  clipsToInsert: Clip[],
  snapIfWithin: number
): Clip[] {
  console.assert(
    isEqual(
      selection.clipIds,
      clipsInSelection.map((clip) => clip.id)
    ),
    "selection.clipIds doesn't match clipsInSelection",
    selection,
    clipsInSelection
  );

  const trimmedStartClip = trimEndIfSplittable(
    clipsInSelection[0],
    selection.startTime,
    snapIfWithin
  );
  const endClip = clipsInSelection[clipsInSelection.length - 1];
  const trimmedEndClip = trimBeginningIfSplittable(
    endClip,
    selection.endTime,
    snapIfWithin
  );

  if (endClip.duration > 0 && endClip.type !== BlockTypes.RECORDING_SESSION) {
    return [trimmedStartClip, ...clipsToInsert, trimmedEndClip];
  } else {
    return [trimmedStartClip, ...clipsToInsert];
  }
}

// TODO: Eliminate so we can make `generateClipTranscript` private again?
function isClipTranscriptionValid(
  clip: ClipWithPlaylistTimes,
  asset: Asset
): boolean {
  if (clip.type !== BlockTypes.TRANSCRIPTION) {
    return true;
  }

  const clipTranscriptSegments = generateClipTranscript(clip, asset);

  return clipTranscriptSegments.length > 0;
}

function clipValidatorWithAssets(assets: AssetSet): (clip: Clip) => boolean {
  return (clip: ClipWithPlaylistTimes): boolean => {
    if (isClipTypeWithoutAssetSupport(clip)) {
      return true;
    }

    if (clip.type === BlockTypes.RECORDING_SESSION) {
      return true;
    }

    if (clip.duration <= 0) {
      return false;
    }

    if (isClipUploading(clip)) {
      return true;
    }

    return (
      isClipWithAsset(clip) &&
      isClipTranscriptionValid(clip, assets[clip.assetId])
    );
  };
}

export function replaceSelectionWithClips(
  clips: Clip[],
  selection: Selection,
  clipsToInsert: Clip[],
  snapIfWithin = 0.1
): AppAction<ReplaceClipsAction> {
  return (dispatch, getState): ReplaceClipsAction => {
    const clipsInSelection = findClipsInSelection(clips, selection);
    const clipsInSelectionTypes = clipsInSelection.map((clip) => clip.type);
    console.assert(
      new Set(clipsInSelectionTypes).size <= 1,
      "Invalid selection crossing multiple block types",
      clipsInSelectionTypes
    );

    if (clipsInSelection.length === 0) {
      return dispatch({
        type: ActionTypes.REPLACE_CLIPS,
        clipsToReplace: [], // See `ClipsReducer.replaceClips`. Empty array will append the `clipsToInsert` to the end of `clips`.
        clipsToInsert,
      });
    }

    const newClips = buildNewClipsToReplaceSelection(
      selection,
      clipsInSelection,
      clipsToInsert,
      snapIfWithin
    );
    const { assets } = getState();
    const clipValidatorMethod = clipValidatorWithAssets(assets);
    const validClips = newClips
      .filter(clipValidatorMethod)
      .map(clipWithoutTimes);

    const clipsToReplace = clipsInSelection.map(clipWithoutTimes);

    // TODO:
    // `validClips` might be empty if the first word of a block is being deleted. It would be
    // nice if we could pull out the next clip to alias that to keep the block id stable.
    // But this information isn't easily available here.
    if (validClips[0] && clipsToReplace[0]) {
      // alias the head of the selection
      BlockIdStabilizer.instance.aliasClip({
        to: validClips[0].id,
        from: clipsToReplace[0].id,
      });

      // alias the tail end of the selection. This is necessary because conversations are
      // single clips. Making the first edit in a transcribed conversation splits one clip
      // into two. The majority of the segments though are not changing, so we need to carry
      // old clip id through to the new tail clip to keep block ids stable and prevent
      // remounting the entire transcript after the edit.
      const lastClipReplaced = clipsToReplace[clipsToReplace.length - 1];
      const lastClipInserted = validClips[validClips.length - 1];
      const lastNewClip = clipsToInsert[clipsToInsert.length - 1];
      if (
        validClips.length > 1 &&
        (lastNewClip === undefined || lastClipInserted.id !== lastNewClip.id)
        // this indicates that a clip at the tail end of the selection is being trimmed
        // we don't want to alias a new clip being inserted at the end.
      ) {
        BlockIdStabilizer.instance.aliasClip({
          from: lastClipReplaced.id,
          to: lastClipInserted.id,
        });
      }
    }

    return dispatch({
      type: ActionTypes.REPLACE_CLIPS,
      clipsToReplace,
      clipsToInsert: validClips,
    });
  };
}

const moveBlockToStartOfTranscription = (
  movingBlock: Block,
  destinationBlock: TranscriptionBlock
): AppAction<Promise<void>> => {
  return moveBlockToToken(
    movingBlock,
    destinationBlock,
    1, // first token is null, moveBlockToToken expects a word token index
    InsertDirection.BEFORE
  );
};

const moveBlockToEndOfTranscription = (
  movingBlock: Block,
  destinationBlock: TranscriptionBlock
): AppAction<Promise<void>> => {
  return moveBlockToToken(
    movingBlock,
    destinationBlock,
    blockTokens(destinationBlock).length - 2, // last token is null, moveBlockToToken expects a word token index
    InsertDirection.AFTER
  );
};

const moveBlockNearTranscription = (
  movingBlock: Block,
  destinationBlock: TranscriptionBlock,
  destinationInsertDirection: InsertDirection
): AppAction<Promise<void>> => {
  if (destinationInsertDirection === InsertDirection.BEFORE) {
    return moveBlockToStartOfTranscription(movingBlock, destinationBlock);
  } else {
    return moveBlockToEndOfTranscription(movingBlock, destinationBlock);
  }
};

export const moveBlock = (
  movingBlock: Block,
  destinationBlock: Block,
  destinationInsertDirection: InsertDirection
): AppAction<Promise<void>> => {
  return async (dispatch, getState): Promise<void> => {
    if (movingBlock.id === destinationBlock.id) {
      return;
    } // Moving block to itself, nothing needs to change

    if (destinationBlock.type === BlockTypes.TRANSCRIPTION) {
      // multiple transcription blocks can originate from a single clip if that clip is
      // from a recording with multiple speakers. Therefore it's not sufficient to use the
      // whole clip based logic below, since we may need to split a clip if we're moving a
      // block in between two speakers.
      return await dispatch(
        moveBlockNearTranscription(
          movingBlock,
          destinationBlock,
          destinationInsertDirection
        )
      );
    }

    const { clips } = getState();
    const movingBlockClips = clipsFromBlock(movingBlock, clips);
    const destinationBlockClips = clipsFromBlock(destinationBlock, clips);

    // Delete the moving block from its original position
    const deleteAction: ReplaceClipsAction = {
      type: ActionTypes.REPLACE_CLIPS,
      clipsToReplace: movingBlockClips,
      clipsToInsert: [],
    };

    await dispatch(deleteAction);

    const beforeDestinationClips =
      destinationInsertDirection === InsertDirection.BEFORE
        ? movingBlockClips
        : [];
    const afterDestinationClips =
      destinationInsertDirection === InsertDirection.AFTER
        ? movingBlockClips
        : [];
    const replaceDestinationBlock =
      destinationInsertDirection === InsertDirection.INSIDE;
    const clipsToInsert = replaceDestinationBlock
      ? movingBlockClips
      : destinationBlockClips;

    // Insert the moving block at its destination position
    const insertAction: ReplaceClipsAction = {
      type: ActionTypes.REPLACE_CLIPS,
      clipsToReplace: destinationBlockClips,
      clipsToInsert: [
        ...beforeDestinationClips,
        ...clipsToInsert,
        ...afterDestinationClips,
      ],
    };

    await dispatch(insertAction);

    await dispatch(saveProject());
  };
};

export const moveBlockToToken = (
  movingBlock: Block,
  destinationBlock: TranscriptionBlock,
  tokenIndex: number,
  insertDirection: InsertDirection
): AppAction<Promise<void>> => {
  return async (dispatch, getState): Promise<void> => {
    const { clips } = getState();

    const movingBlockClips = clipsFromBlock(movingBlock, clips);
    const targetTokenIndex =
      insertDirection === InsertDirection.AFTER
        ? tokenIndex + 1
        : tokenIndex - 1;
    const targetToken = blockTokens(destinationBlock)[targetTokenIndex];
    const tokenMidpoint =
      (targetToken.playlistStartTime + targetToken.playlistEndTime) / 2;

    // need to re-insert clips first otherwise playlist time changes
    // but need to change ids otherwise we will have multiple clips
    // with the same id.
    const movingBlockClipsNewIds = movingBlockClips.map((clip) => ({
      ...clip,
      id: uuidv4(),
    }));
    await dispatch(insertClipsAtTime(movingBlockClipsNewIds, tokenMidpoint));

    // Delete the moving block from its original position
    await dispatch({
      type: ActionTypes.REPLACE_CLIPS,
      clipsToReplace: movingBlockClips,
      clipsToInsert: [],
    });

    await dispatch(saveProject());
  };
};
